import { useCallback, useEffect, useMemo, useState } from "react";
import { COLORS } from "../../../constants/design/colors";
import { useInstrumentResolver } from "../../../hooks/markets/useInstrumentResolver";
import { formatCurrencyWithSubscript } from "../../../utils/format";
import { copyStringToClipboard } from "../../../utils/strings";
import {
  getTradeGrossPnl,
  getTradeBorrowingFee,
  getTradeFundingFee,
  getTradeNetPnl,
  getTradePositionFee,
  getTradeTotalFees,
  hasNonZeroTradeValue,
} from "../../../utils/trade";
import { PnLCardModal } from "../../PnLCardModal";
import { ITradeDetailsModalProps } from "./TradeDetailsModal.types";
import TradeDetailsModalContent from "./TradeDetailsModalContent";

function TradeDetailsModal({ trade, onHide, style }: ITradeDetailsModalProps) {
  const [showCopied, setShowCopied] = useState(false);
  const [showPnLCard, setShowPnLCard] = useState<boolean>(false);
  const { resolveInstrumentName, resolveMarketPrecision } =
    useInstrumentResolver();
  const resolvedTrade = useMemo(
    () =>
      trade
        ? {
            ...trade,
            instrument_name: resolveInstrumentName(trade) || trade.instrument_name,
          }
        : undefined,
    [resolveInstrumentName, trade]
  );
  const { amount_precision, price_precision } = useMemo(
    () =>
      resolvedTrade
        ? resolveMarketPrecision(resolvedTrade)
        : { amount_precision: 2, price_precision: 2 },
    [resolvedTrade, resolveMarketPrecision]
  );
  const indexPrice = resolvedTrade?.index_price || resolvedTrade?.spot_price;
  const grossPnl = getTradeGrossPnl(resolvedTrade);
  const netPnl = getTradeNetPnl(resolvedTrade);
  const totalFees = getTradeTotalFees(resolvedTrade);
  const positionFee = getTradePositionFee(resolvedTrade);
  const borrowingFee = getTradeBorrowingFee(resolvedTrade);
  const fundingFee = getTradeFundingFee(resolvedTrade);

  const renderSignedPnl = (value?: string) =>
    hasNonZeroTradeValue(value) ? (
      <span
        style={{
          color:
            Number(value) >= 0 ? COLORS.up.one : COLORS.down.one,
        }}
      >
        {formatCurrencyWithSubscript(value || 0, {
          precision: price_precision,
          showPositiveSign: true,
        })}
      </span>
    ) : (
      <span>-</span>
    );

  const renderFee = (value?: string, signed: boolean = false) => {
    if (!hasNonZeroTradeValue(value)) {
      return <span>-</span>;
    }

    const rawValue = Number(value);
    const displayValue = signed ? -rawValue : rawValue;
    return (
      <span
        style={
          signed
            ? {
                color:
                  displayValue >= 0 ? COLORS.up.one : COLORS.down.one,
              }
            : undefined
        }
      >
        {formatCurrencyWithSubscript(displayValue, {
          precision: price_precision,
          showPositiveSign: signed,
        })}
      </span>
    );
  };

  // Dismiss copied text after 1 second
  useEffect(() => {
    if (showCopied) {
      const timeout = setTimeout(() => {
        setShowCopied(!showCopied);
      }, 500);
      return () => clearTimeout(timeout);
    }
    return () => {};
  }, [showCopied]);

  const onCopyTradeId = useCallback(() => {
    if (trade) {
      copyStringToClipboard(trade.trade_id);
      setShowCopied(true);
    }
  }, [trade]);

  const onOpenPnLCard = useCallback(() => {
    setShowPnLCard(true);
  }, []);

  const onHidePnLCard = useCallback(() => {
    setShowPnLCard(false);
  }, []);

  if (!showPnLCard) {
    return (
      <TradeDetailsModalContent
        resolvedTrade={resolvedTrade}
        style={style}
        onHide={onHide}
        amount_precision={amount_precision}
        price_precision={price_precision}
        indexPrice={indexPrice}
        grossPnl={grossPnl}
        netPnl={netPnl}
        totalFees={totalFees}
        positionFee={positionFee}
        borrowingFee={borrowingFee}
        fundingFee={fundingFee}
        showCopied={showCopied}
        onCopyTradeId={onCopyTradeId}
        onOpenPnLCard={onOpenPnLCard}
        renderSignedPnl={renderSignedPnl}
        renderFee={renderFee}
      />
    );
  }

  return (
    <PnLCardModal
      pnlType="trade"
      pnlData={resolvedTrade}
      onHideModal={onHide}
      show={!!resolvedTrade}
      onHidePnLCard={onHidePnLCard}
    />
  );
}

export default TradeDetailsModal;
